import React, { useState } from 'react';
import { Container, Form, Button, Card, Spinner, Stack, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import { processInitialCommissions, generateFinalReports } from '../api';
import ReviewCommissionsTable from './ReviewCommissionsTable';

const CommissionsPage = () => {
  const [voucherFile, setVoucherFile] = useState(null);
  const [conductoresFile, setConductoresFile] = useState(null);
  const [reviewedFile, setReviewedFile] = useState(null);
  
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const [comisionesValidas, setComisionesValidas] = useState([]);
  const [comisionesParaRevisar, setComisionesParaRevisar] = useState([]);
  const [reviewedCommissions, setReviewedCommissions] = useState([]);

  const handleFileChange = (event, fileType) => {
    const file = event.target.files[0];
    if (fileType === 'voucher') {
      setVoucherFile(file);
    } else if (fileType === 'conductores') {
      setConductoresFile(file);
    }
  };

  const handleReviewedFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) {
        setReviewedFile(null);
        setReviewedCommissions([]);
        return;
    }
    setReviewedFile(file);

    Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: "UTF-8",
        complete: (results) => {
            const parsedData = results.data.map(row => {
                const newRow = {};
                for (const key in row) {
                    const newKey = key.trim();
                    newRow[newKey] = row[key];
                }

                return {
                    ...newRow,
                    ID: newRow.ID ? parseInt(newRow.ID, 10) : null,
                    Monto: newRow.Monto ? parseFloat(newRow.Monto) : null,
                }
            });
            setReviewedCommissions(parsedData);
            toast.success(`Archivo de revisión cargado. Se encontraron ${parsedData.length} filas.`);
        },
        error: (error) => {
            toast.error(`Error al leer el archivo CSV: ${error.message}`);
            setReviewedCommissions([]);
        }
    });
  };

  const handleProcess = async () => {
    if (!voucherFile || !conductoresFile) {
      toast.error('Por favor, selecciona ambos archivos.');
      return;
    }

    setIsLoading(true);
    setComisionesValidas([]);
    setComisionesParaRevisar([]);
    setReviewedFile(null);
    setReviewedCommissions([]);
    // Reset file inputs visually
    document.getElementById('voucherFile').value = null;
    document.getElementById('conductoresFile').value = null;


    try {
      const response = await processInitialCommissions(voucherFile, conductoresFile);
      setComisionesValidas(response.comisiones_validas || []);
      setComisionesParaRevisar(response.comisiones_para_revisar || []);
      toast.success('Archivos procesados con éxito! Revisa las tablas a continuación.');
    } catch (error) {
      // Error is handled by the api.js helper
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadReviewFile = () => {
    if (comisionesParaRevisar.length === 0) {
        toast.warn("No hay comisiones para descargar.");
        return;
    }
    const csv = Papa.unparse(comisionesParaRevisar, { delimiter: ';' });
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'comisiones_para_revisar.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Archivo de revisión descargado (comisiones_para_revisar.csv)");
  };

  const handleDownloadValidFile = () => {
    if (comisionesValidas.length === 0) {
        toast.warn("No hay comisiones válidas para descargar.");
        return;
    }
    const csv = Papa.unparse(comisionesValidas, { delimiter: ';' });
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'comisiones_validas.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success("Archivo de comisiones válidas descargado (comisiones_validas.csv)");
  };

  const handleGenerateFinalReports = async () => {
    const payload = {
      comisiones_validas_originales: comisionesValidas,
      comisiones_aprobadas: reviewedCommissions,
    };

    if (payload.comisiones_aprobadas.length === 0 && payload.comisiones_validas_originales.length === 0) {
        toast.warn("No hay comisiones válidas o aprobadas para generar reportes.");
        return;
    }

    setIsGenerating(true);
    try {
        const blob = await generateFinalReports(payload);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'reportes.zip';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Reportes generados y descargados con éxito!");
    } catch (error) {
        // Error is handled by the api.js helper
    } finally {
        setIsGenerating(false);
    }
  };

  const showResults = comisionesValidas.length > 0 || comisionesParaRevisar.length > 0;

  return (
    <Container className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>Herramienta de Comisiones en RD</h1>
            <Link to="/" className="btn btn-outline-secondary">
                &larr; Volver al Gestor
            </Link>
        </div>

        <Card className="mb-4">
            <Card.Body>
                <Card.Title>Fase 1: Procesamiento Inicial</Card.Title>
                <Card.Text>
                    Sube los archivos <code>ProcesarVoucher.xlsx</code> y <code>Conductores.xlsx</code> para iniciar el proceso de validación.
                </Card.Text>
                <Form>
                    <Form.Group controlId="voucherFile" className="mb-3">
                        <Form.Label>Archivo de Vouchers (.xlsx)</Form.Label>
                        <Form.Control type="file" accept=".xlsx" onChange={(e) => handleFileChange(e, 'voucher')} />
                    </Form.Group>

                    <Form.Group controlId="conductoresFile" className="mb-3">
                        <Form.Label>Archivo de Conductores (.xlsx)</Form.Label>
                        <Form.Control type="file" accept=".xlsx" onChange={(e) => handleFileChange(e, 'conductores')} />
                    </Form.Group>

                    <Button variant="primary" onClick={handleProcess} disabled={isLoading || isGenerating}>
                        {isLoading ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Procesando...</> : 'Procesar Archivos'}
                    </Button>
                </Form>
            </Card.Body>
        </Card>

        {showResults && (
          <Stack gap={4}>
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <Card.Title as="h5" className="mb-0">Comisiones Válidas ({comisionesValidas.length})</Card.Title>
                    <Button variant="info" size="sm" onClick={handleDownloadValidFile} disabled={comisionesValidas.length === 0}>
                        Descargar Válidas (.csv)
                    </Button>
                </Card.Header>
            </Card>

            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <Card.Title as="h5" className="mb-0">Comisiones Para Revisión ({comisionesParaRevisar.length})</Card.Title>
                    <Button variant="secondary" size="sm" onClick={handleDownloadReviewFile} disabled={comisionesParaRevisar.length === 0}>
                        Descargar para Revisión (.csv)
                    </Button>
                </Card.Header>
                <Card.Body>
                    <ReviewCommissionsTable comisiones={comisionesParaRevisar} />
                </Card.Body>
            </Card>

            <Card>
                <Card.Body>
                    <Card.Title>Fase 2: Carga de Revisiones y Generación Final</Card.Title>
                    <Card.Text>
                        Una vez hayas editado el archivo <code>comisiones_para_revisar.txt</code> (dejando solo las filas que apruebas), súbelo aquí.
                    </Card.Text>
                    <Form.Group controlId="reviewedFile" className="mb-3">
                        <Form.Label>Archivo de Revisión (.txt o .csv)</Form.Label>
                        <Form.Control type="file" accept=".csv,.txt" onChange={handleReviewedFileChange} />
                    </Form.Group>
                    {reviewedFile && (
                        <Alert variant="info">
                            Archivo <strong>{reviewedFile.name}</strong> cargado. Se han detectado <strong>{reviewedCommissions.length}</strong> comisiones aprobadas.
                        </Alert>
                    )}
                </Card.Body>
            </Card>
            
            <div className="d-flex justify-content-end">
                <Button 
                    variant="success" 
                    onClick={handleGenerateFinalReports} 
                    disabled={isLoading || isGenerating || (reviewedCommissions.length === 0 && comisionesValidas.length === 0)}
                >
                    {isGenerating ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Generando...</> : 'Generar Reportes Finales'}
                </Button>
            </div>
          </Stack>
        )}
    </Container>
  );
};

export default CommissionsPage;